import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import {
	ArrowRightIcon,
	CalendarIcon,
	HoverTextTooltip,
	Tag,
	TagProps,
	Text,
	colors,
	spacing,
} from '@pletly/ui';
import { getName } from '@pletly/shared';

import { getFormattedTimestamp } from '%helpers/date';
import { Post, PostReviewStatus } from '%__generated__/types';
import { UserAvatar } from '%components';
import { PostContent } from '%components/Posts/Post/content';
import { PostSelectedUsersInfo } from '../PostSelectedUsersInfo';

interface Props {
	post: Omit<Post, 'comments' | 'likes' | 'viewCount'>;
	children?: React.ReactNode;
}

export const PostItem = ({ post, children }: Props) => {
	const { t } = useTranslation();
	const tagPropsByStatus: { [key in PostReviewStatus]: Pick<TagProps, 'color' | 'text'> } = useMemo(
		() => ({
			[PostReviewStatus.Pending]: { text: t('post.review.status.pending') },
			[PostReviewStatus.Rejected]: { text: t('post.review.status.rejected'), color: 'danger' },
			[PostReviewStatus.Approved]: { text: t('post.review.status.approved') },
		}),
		[t],
	);

	const { author, selectedUsers, review } = post;

	return (
		<View style={styles.postContainer}>
			<View style={styles.postHeader}>
				<View style={styles.postHeaderSection}>
					<UserAvatar username={getName(author)} size={48} src={post.author?.photoUrl} />
					<View>
						<View style={styles.postAuthorAndTargetUsers}>
							<Text category="h4">{getName(author)}</Text>
							<ArrowRightIcon />
							<PostSelectedUsersInfo users={selectedUsers?.map(({ user }) => user)} />
						</View>
						<Text>{author?.email ?? ''}</Text>
					</View>
				</View>
				<View style={styles.postHeaderSection}>
					{post.publishAt && <ScheduledPostTooltip date={new Date(post.publishAt)} />}
					{review && <Tag {...tagPropsByStatus[review.status]} />}
				</View>
			</View>
			<PostContent content={post.content} mediaFiles={post.mediaFiles} type={post.type} />
			{children}
		</View>
	);
};

const ScheduledPostTooltip = ({ date }: { date: Date }) => {
	const { t } = useTranslation();

	return (
		<HoverTextTooltip
			content={`${t('post.new.to-be-published')}: ${getFormattedTimestamp(date)}`}
			style={styles.scheduledPostTooltip}
		>
			<CalendarIcon />
		</HoverTextTooltip>
	);
};

const styles = StyleSheet.create({
	postContainer: {
		backgroundColor: colors.white,
		borderRadius: 32,
		padding: spacing(4),
		gap: spacing(4),
	},
	postHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	postHeaderSection: {
		flexDirection: 'row',
		gap: spacing(2),
		alignItems: 'center',
	},
	postAuthorAndTargetUsers: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing(),
	},
	scheduledPostTooltip: {
		minWidth: 300,
	},
});
